import i18n from '@/i18n'
import { useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import {
  assertContactInScope,
  assertOpportunityInScope,
} from '@/lib/chatContactScope'
import {
  formatCrmInteractionDateTime,
  getPipelineStageNames,
  logCrmContactInteraction,
} from '@/lib/crmInteractionLog'

export interface OpportunityData {
  id: string
  pipeline_id: string
  stage_id: string
  title?: string
  pipeline_name?: string
  stage_name?: string
}

export interface WonOpportunityData {
  product?: string
  price?: number
  sale_date?: string
  won_by_user_id?: string
  notes?: string
}

interface UseContactOpportunityReturn {
  creating: boolean
  updating: boolean
  deleting: boolean
  closing: boolean
  error: string | null
  createOpportunity: (contactId: string, pipelineId: string, stageId: string) => Promise<string | null>
  updateOpportunityStage: (opportunityId: string, contactId: string, pipelineId: string, stageId: string) => Promise<boolean>
  getContactOpportunities: (contactId: string) => Promise<OpportunityData[]>
  deleteOpportunity: (opportunityId: string) => Promise<boolean>
  closeOpportunity: (
    opportunityId: string,
    contactId: string,
    stageId: string,
    status: 'won' | 'lost',
    lossReasonId?: string,
    wonData?: WonOpportunityData,
    lostByUserId?: string
  ) => Promise<boolean>
}

export const useContactOpportunity = (): UseContactOpportunityReturn => {
  const { user } = useAuth()
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [closing, setClosing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Busca todas as oportunidades do contato com nomes de pipeline e stage.
   * O mesmo contato pode ter vários cards, inclusive no mesmo funil.
   */
  const getContactOpportunities = async (contactId: string): Promise<OpportunityData[]> => {
    if (!user?.account_id || !supabase) return []

    try {
      const { data, error: fetchError } = await supabase
        .from('account_opportunities')
        .select(`
          id,
          pipeline_id,
          stage_id,
          title,
          account_pipelines!inner(name),
          account_pipeline_stages!inner(name)
        `)
        .eq('contact_id', contactId)
        .eq('account_id', user.account_id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      type OpportunityRow = {
        id: string
        pipeline_id: string
        stage_id: string
        title: string
        account_pipelines?: { name?: string } | null
        account_pipeline_stages?: { name?: string } | null
      }

      return ((data || []) as OpportunityRow[]).map((row) => ({
        id: row.id,
        pipeline_id: row.pipeline_id,
        stage_id: row.stage_id,
        title: row.title,
        pipeline_name: row.account_pipelines?.name,
        stage_name: row.account_pipeline_stages?.name,
      }))
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao buscar oportunidades:', err)
      return []
    }
  }

  /**
   * Cria uma nova oportunidade para o contato no funil/etapa informados.
   * Sempre insere — o mesmo contato pode ter vários cards no mesmo funil.
   */
  const createOpportunity = async (
    contactId: string,
    pipelineId: string,
    stageId: string
  ): Promise<string | null> => {
    if (!user?.account_id || !supabase) return null

    try {
      setError(null)
      setCreating(true)

      const inScope = await assertContactInScope(
        supabase,
        contactId,
        user.account_id,
        user ?? null,
      )
      if (!inScope) {
        setError(i18n.t('crm:errors.noPermissionContactOpportunities'))
        return null
      }

      // Buscar informações do contato para criar título
      const { data: contact, error: contactError } = await supabase
        .from('account_contacts')
        .select('name, jid')
        .eq('id', contactId)
        .eq('account_id', user.account_id)
        .maybeSingle()

      if (contactError) throw contactError

      const contactName = contact?.name || contact?.jid?.split('@')[0] || 'Contato'
      const title = `Oportunidade - ${contactName}`

      const { data: newOpp, error: insertError } = await supabase
        .from('account_opportunities')
        .insert({
          account_id: user.account_id,
          contact_id: contactId,
          pipeline_id: pipelineId,
          stage_id: stageId,
          title: title,
          price: 0
        })
        .select('id')
        .single()

      if (insertError) throw insertError

      const stageNames = await getPipelineStageNames([stageId])
      const toStage = stageNames.get(stageId) || 'nova etapa'
      const userName = user.name || 'Usuário'
      const when = formatCrmInteractionDateTime()

      await logCrmContactInteraction({
        accountId: user.account_id,
        contactId,
        title: 'Oportunidade criada',
        description: `${userName} criou a oportunidade "${title}" na etapa "${toStage}" em ${when}`,
      })

      console.log('✅ [useContactOpportunity] Oportunidade criada:', {
        opportunityId: newOpp.id,
        contactId,
        pipelineId,
        stageId,
        title
      })

      return newOpp.id as string
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao criar oportunidade:', err)
      setError(err.message || 'Erro ao processar oportunidade')
      return null
    } finally {
      setCreating(false)
    }
  }

  /**
   * Move uma oportunidade existente (identificada pelo id) de etapa/funil.
   */
  const updateOpportunityStage = async (
    opportunityId: string,
    contactId: string,
    pipelineId: string,
    stageId: string
  ): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setError(null)
      setUpdating(true)

      const inScope = await assertOpportunityInScope(
        supabase,
        opportunityId,
        user.account_id,
        user ?? null,
      )
      if (!inScope) {
        setError(i18n.t('crm:errors.noPermissionMoveOpportunity'))
        return false
      }

      const { data: current, error: fetchError } = await supabase
        .from('account_opportunities')
        .select('stage_id')
        .eq('id', opportunityId)
        .eq('account_id', user.account_id)
        .maybeSingle()

      if (fetchError) throw fetchError

      const previousStageId = current?.stage_id

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update({
          pipeline_id: pipelineId,
          stage_id: stageId,
          updated_at: new Date().toISOString()
        })
        .eq('id', opportunityId)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      if (previousStageId && previousStageId !== stageId) {
        const stageNames = await getPipelineStageNames([previousStageId, stageId])
        const fromStage = stageNames.get(previousStageId) || 'etapa anterior'
        const toStage = stageNames.get(stageId) || 'nova etapa'
        const userName = user.name || 'Usuário'
        const when = formatCrmInteractionDateTime()

        await logCrmContactInteraction({
          accountId: user.account_id,
          contactId,
          title: 'Movimentação no CRM',
          description: `${userName} moveu a oportunidade de "${fromStage}" para "${toStage}" em ${when}`,
        })
      }

      console.log('✅ [useContactOpportunity] Oportunidade atualizada:', {
        opportunityId,
        contactId,
        pipelineId,
        stageId
      })

      return true
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao atualizar oportunidade:', err)
      setError(err.message || 'Erro ao processar oportunidade')
      return false
    } finally {
      setUpdating(false)
    }
  }

  /**
   * Remove uma oportunidade (delete permanente)
   */
  const deleteOpportunity = async (opportunityId: string): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setDeleting(true)
      setError(null)

      const inScope = await assertOpportunityInScope(
        supabase,
        opportunityId,
        user.account_id,
        user ?? null,
      )
      if (!inScope) {
        setError(i18n.t('crm:errors.noPermissionRemoveOpportunity'))
        return false
      }

      const { error: deleteError } = await supabase
        .from('account_opportunities')
        .delete()
        .eq('id', opportunityId)
        .eq('account_id', user.account_id)

      if (deleteError) throw deleteError

      console.log('✅ [useContactOpportunity] Oportunidade removida:', opportunityId)
      return true
    } catch (err: any) {
      console.error('💥 [useContactOpportunity] Erro ao remover oportunidade:', err)
      setError(err.message || 'Erro ao remover oportunidade')
      return false
    } finally {
      setDeleting(false)
    }
  }

  const closeOpportunity = async (
    opportunityId: string,
    contactId: string,
    stageId: string,
    status: 'won' | 'lost',
    lossReasonId?: string,
    wonData?: WonOpportunityData,
    lostByUserId?: string
  ): Promise<boolean> => {
    if (!user?.account_id || !supabase) return false

    try {
      setClosing(true)
      setError(null)

      const inScope = await assertOpportunityInScope(
        supabase,
        opportunityId,
        user.account_id,
        user ?? null,
      )
      if (!inScope) {
        setError(i18n.t('crm:errors.noPermissionContactOpportunities'))
        return false
      }

      const { data: existingOpp, error: fetchError } = await supabase
        .from('account_opportunities')
        .select('id, stage_id, title')
        .eq('id', opportunityId)
        .eq('account_id', user.account_id)
        .maybeSingle()

      if (fetchError) throw fetchError

      const updateData: Record<string, unknown> = {
        stage_id: stageId,
        status: status,
        updated_at: new Date().toISOString(),
        closed_at: new Date().toISOString(),
      }

      // Each outcome clears the other one's fields to avoid contradictory records
      if (status === 'lost') {
        if (lossReasonId) updateData.loss_reason_id = lossReasonId
        updateData.lost_by_user_id = lostByUserId ?? null
        updateData.won_by_user_id = null
        updateData.sale_date = null
      }

      if (status === 'won') {
        updateData.loss_reason_id = null
        updateData.lost_by_user_id = null

        if (wonData) {
          updateData.product = wonData.product
          updateData.price = wonData.price
          updateData.sale_date = wonData.sale_date
          updateData.won_by_user_id = wonData.won_by_user_id
          updateData.notes = wonData.notes
        }
      }

      if (!existingOpp) {
        setError(i18n.t('crm:errors.opportunityNotFound'))
        return false
      }

      const oppLabel = existingOpp.title || 'Oportunidade'
      const previousStageId = existingOpp.stage_id

      const { error: updateError } = await supabase
        .from('account_opportunities')
        .update(updateData)
        .eq('id', existingOpp.id)
        .eq('account_id', user.account_id)

      if (updateError) throw updateError

      const stageNames = await getPipelineStageNames([previousStageId, stageId])
      const fromStage = previousStageId
        ? stageNames.get(previousStageId) || 'etapa anterior'
        : null
      const toStage = stageNames.get(stageId) || 'nova etapa'
      const userName = user.name || 'Usuário'
      const when = formatCrmInteractionDateTime()
      const action = status === 'won' ? 'ganha' : 'perdida'
      const stagePart = fromStage
        ? `de "${fromStage}" para "${toStage}"`
        : `na etapa "${toStage}"`

      await logCrmContactInteraction({
        accountId: user.account_id,
        contactId,
        title: status === 'won' ? 'Oportunidade ganha' : 'Oportunidade perdida',
        description: `${userName} marcou a oportunidade "${oppLabel}" como ${action} ${stagePart} em ${when}`,
      })

      return true
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao fechar oportunidade'
      console.error('[useContactOpportunity] Erro ao fechar oportunidade:', err)
      setError(errorMessage)
      return false
    } finally {
      setClosing(false)
    }
  }

  return {
    creating,
    updating,
    deleting,
    closing,
    error,
    createOpportunity,
    updateOpportunityStage,
    getContactOpportunities,
    deleteOpportunity,
    closeOpportunity,
  }
}
